import { Magnet, MousePointer2, Pen, Ruler } from "lucide-react";
import { getSceneGridConfig } from "../lib/grid-utils";
import { updateBlock } from "../state/graph-actions";
import { useBlock, useCurrentSceneId } from "../state/graph-hooks";
import { useToolStore } from "../state/tool-store";
import { useYDoc } from "../state/yjs-provider";
import { IconButton } from "./icon-button";

export function FloatingToolbar() {
  const { activeTool, setTool } = useToolStore();
  const doc = useYDoc();
  const sceneId = useCurrentSceneId();
  const sceneNode = useBlock<{
    properties?: Record<string, unknown>;
    type?: string;
  }>(sceneId);
  const { snapToGrid } = getSceneGridConfig(
    sceneNode?.properties as Record<string, unknown> | undefined,
  );

  const canToggleSnap = Boolean(sceneNode && sceneNode.type === "location");

  const toggleSnapToGrid = () => {
    if (!sceneNode || !canToggleSnap) return;
    const nextProperties = {
      ...(sceneNode.properties ?? {}),
      snapToGrid: !snapToGrid,
    };

    updateBlock(doc, sceneId, {
      properties: nextProperties,
    } as { properties: Record<string, unknown> });
  };

  const tools = [
    { id: "select" as const, icon: MousePointer2, label: "Select" },
    { id: "draw" as const, icon: Pen, label: "Draw" },
    { id: "measure" as const, icon: Ruler, label: "Measure" },
  ];

  return (
    <div className="absolute top-2 left-1/2 -translate-x-1/2 pointer-events-auto z-50">
      <div className="flex flex-row gap-px rounded bg-black/40 backdrop-blur-sm border border-white/10 p-[2px]">
        {tools.map(({ id, icon: Icon, label }) => (
          <IconButton
            key={id}
            tooltip={label}
            className={`flex items-center justify-center w-6 h-5 rounded transition-colors ${
              activeTool === id
                ? "bg-white/15 text-white"
                : "text-white/40 hover:text-white/70 hover:bg-white/8"
            }`}
            onClick={() => setTool(id)}
          >
            <Icon className="w-3 h-3" />
          </IconButton>
        ))}
        <IconButton
          tooltip="Snap to grid"
          className={`flex items-center justify-center w-6 h-5 rounded transition-colors ${
            snapToGrid
              ? "bg-sky-400/20 text-sky-200"
              : "text-white/40 hover:text-white/70 hover:bg-white/8"
          } ${!canToggleSnap ? "opacity-50" : ""}`}
          onClick={toggleSnapToGrid}
          disabled={!canToggleSnap}
        >
          <Magnet className="w-3 h-3" />
        </IconButton>
      </div>
    </div>
  );
}
